/**
 * The Get A Map mark.
 *
 * One geometric idea, drawn twice: as SVG here for the page, and by
 * tools/make-icons.ts as rasterised PNGs for the PWA/home-screen icons. The
 * shapes are deliberately simple — a pin, a hole, a folded-map baseline —
 * because the mark has to survive being 16 pixels wide in a browser tab, and
 * anything with fine detail turns to mush at that size.
 *
 * Colors come from the app's existing palette (navy → sea gradient, gold
 * accent) rather than a new brand palette, so the mark sits inside the UI it
 * came from.
 */

export const BRAND = "Get A Map";

/** Palette, duplicated as literals in the icon generator — keep them in step. */
export const LOGO_COLORS = {
  navy: "#14436b",
  sea: "#0f7b8f",
  gold: "#e0b24b",
  paper: "#ffffff",
};

/**
 * The square app mark. `size` is the CSS pixel box; the geometry is defined
 * in a 0-100 viewBox so it scales cleanly.
 *
 * `tile` draws the rounded gradient plate (icons, favicons). Without it the
 * pin is drawn alone in currentColor-ish brand tones, which is what the hero
 * lockup wants — a plate inside a plate reads as a sticker.
 */
export function logoMark(size = 32, tile = true): string {
  const id = `gam${Math.round(size)}${tile ? "t" : ""}`;
  return `<svg class="gam-mark" width="${size}" height="${size}" viewBox="0 0 100 100" role="img" aria-label="${BRAND}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="${id}g" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${LOGO_COLORS.navy}"/>
      <stop offset="100%" stop-color="${LOGO_COLORS.sea}"/>
    </linearGradient>
  </defs>
  ${tile ? `<rect x="0" y="0" width="100" height="100" rx="22" fill="url(#${id}g)"/>` : ""}
  <!-- the fold line: a map's horizon, and it keeps the pin from floating -->
  <path d="M18 74 L38 68 L62 76 L82 70" fill="none"
        stroke="${tile ? "rgba(255,255,255,.45)" : LOGO_COLORS.sea}" stroke-width="5"
        stroke-linecap="round" stroke-linejoin="round"/>
  <!-- pin: circle + tapered point, drawn as one path so the joint is clean -->
  <path d="M50 18 a19 19 0 0 1 19 19 c0 13 -12 22 -19 31 c-7 -9 -19 -18 -19 -31 a19 19 0 0 1 19 -19 z"
        fill="${tile ? LOGO_COLORS.paper : LOGO_COLORS.navy}"/>
  <circle cx="50" cy="37" r="7.5" fill="${tile ? LOGO_COLORS.sea : LOGO_COLORS.gold}"/>
</svg>`;
}

/** Mark + wordmark, for the hero and any page header. */
export function logoLockup(size = 34): string {
  return `<span class="gam-lockup">${logoMark(size, true)}<span class="gam-word">${BRAND}</span></span>`;
}
